import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Bot, Check, MessageSquare } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Spinner } from '../../../components/ui/Spinner';
import { describeInviteState } from '../../../lib/leadBot/testerInvite';
import type {
  InviteRedeemResult,
  PublicInviteStatus,
} from '../../../types/botTesterInvite';
import { fetchInviteStatus, redeemInvite } from './publicApi';

// SMS Lead Bot — the PUBLIC tester sign-up page at /bot-test/:token, rendered
// OUTSIDE SignedInGuard + AppShell so a client's tester never sees the agency
// app chrome.
//
// ⚠️⚠️ THE COPY ON THIS PAGE IS LOAD-BEARING, NOT DECORATION. Someone is about
// to be texted by an AI on a client's behalf. Four things have to be said in
// plain words, BEFORE the button, and each is asserted by a test:
//
//   1. It is an AI ASSISTANT, not a person. Anything else is a small deception
//      that gets found out on the first reply.
//   2. They will receive an SMS at the number they type. Nobody should discover
//      that by receiving one.
//   3. ⚠️ ENTER YOUR OWN MOBILE, NOT A COLLEAGUE'S. This is the whole reason
//      the page exists in this shape — the guard against texting a stranger is
//      the invite link plus the person's own honesty, and the second half only
//      works if we ask.
//   4. HOW TO STOP. Reply STOP. That is already wired all the way through, and
//      it is the tester's exit — telling them is the difference between a trial
//      and something they cannot get out of.
//
// ⚠️ The link's state is resolved by the SERVER on load AND re-resolved on
// submit. A revoked link that someone left open in a tab must not still work.

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
      <div className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        {children}
      </div>
    </div>
  );
}

function Dead({ title, detail }: { title: string; detail: string }) {
  return (
    <Shell>
      <div className="text-center">
        <h1 className="text-lg font-semibold text-slate-900">{title}</h1>
        <p className="mt-2 text-sm text-slate-600">{detail}</p>
      </div>
    </Shell>
  );
}

export function TesterInvitePage() {
  const { token = '' } = useParams<{ token: string }>();
  const [status, setStatus] = useState<PublicInviteStatus | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [clientName, setClientName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<Extract<InviteRedeemResult, { ok: true }> | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setStatus(await fetchInviteStatus(token));
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const result = await redeemInvite(token, {
        name,
        phone,
        clientName: clientName.trim() === '' ? null : clientName.trim(),
      });
      if (result.ok) {
        setDone(result);
        return;
      }
      setError(result.detail);
      // ⚠️ A link that has since been revoked, expired or run out re-renders the
      // page in its real state rather than leaving a form that cannot succeed.
      if (result.reason === 'invalid_state') void load();
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Shell>
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      </Shell>
    );
  }

  if (loadError) {
    return <Dead title="We could not load this page" detail={loadError} />;
  }

  if (!status || status.status !== 'ok') {
    const { title, detail } = describeInviteState(status?.status ?? 'not_found');
    return <Dead title={title} detail={detail} />;
  }

  if (done) {
    return (
      <Shell>
        <div className="text-center">
          <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-kyck-50 text-kyck-600">
            <Check className="h-5 w-5" />
          </div>
          <h1 className="text-lg font-semibold text-slate-900">You're set up</h1>
          <p className="mt-2 text-sm text-slate-600">
            {done.messageSent
              ? `Look out for a text at ${done.phone} in a moment — reply to it and the assistant will reply back.`
              : `We've added ${done.phone}. Your first message will arrive shortly.`}
          </p>
          {/* ⚠️ REPEATED HERE ON PURPOSE. This is the screen someone will still
              be looking at when the first text lands. */}
          <p className="mt-4 rounded-md bg-slate-50 p-3 text-xs text-slate-600">
            You're texting an AI assistant, not a person. Reply <strong>STOP</strong> at any time
            and we won't message you again.
          </p>
        </div>
      </Shell>
    );
  }

  const businessName = status.accountName ?? 'our client';

  return (
    <Shell>
      <div className="mb-4 flex items-center gap-2">
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-kyck-50 text-kyck-600">
          <Bot className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-base font-semibold text-slate-900">Try the {businessName} assistant</h1>
          <p className="text-xs text-slate-500">A test run, by text message</p>
        </div>
      </div>

      {status.isDemo ? (
        <p className="mb-3 rounded-md bg-amber-50 p-3 text-sm text-amber-900">
          <strong>This is a live demo.</strong> {businessName} is a demo club and bookings aren't real.
        </p>
      ) : null}

      {/* ⚠️⚠️ THE FOUR THINGS, BEFORE THE BUTTON. Each is pinned by a test. */}
      <div className="space-y-2 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
        <p className="flex gap-2">
          <Bot className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
          <span>
            <strong>{status.assistantName} is an AI assistant, not a person.</strong> It answers
            questions about {businessName} and hands over to the team when it should.
          </span>
        </p>
        <p className="flex gap-2">
          <MessageSquare className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
          <span>
            <strong>You'll get a text message</strong> at the number you enter below, and standard
            message rates apply.
          </span>
        </p>
        <p className="pl-6">
          <strong>Enter your own mobile — not a colleague's.</strong> Only put in a number you can
          answer yourself.
        </p>
        <p className="pl-6">
          This is a test. <strong>Reply STOP</strong> to any message and we won't text you again.
        </p>
      </div>

      {status.isDemo ? (
        <p className="mt-3 text-sm text-slate-700">
          Enter your mobile and {status.assistantName} will text you. Reply STOP any time to stop.
        </p>
      ) : null}

      <div className="mt-4 space-y-3">
        <div>
          <Label htmlFor="tester-name">Your name</Label>
          <Input
            id="tester-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Alex"
            autoComplete="name"
          />
        </div>
        <div>
          <Label htmlFor="tester-phone">Your mobile number</Label>
          <Input
            id="tester-phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="0412 345 678"
            inputMode="tel"
            autoComplete="tel"
          />
        </div>
        {status.isDemo ? null : (
          <div>
            <Label htmlFor="tester-client">Which business are you from? (optional)</Label>
            <Input
              id="tester-client"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              placeholder="Camperagent"
            />
          </div>
        )}
      </div>

      {error && (
        <p role="alert" className="mt-3 rounded-md bg-rose-50 p-3 text-sm text-rose-700">
          {error}
        </p>
      )}

      <Button
        className="mt-4 w-full"
        onClick={() => void submit()}
        disabled={submitting || name.trim() === '' || phone.trim() === ''}
      >
        {submitting ? <Spinner className="h-4 w-4" /> : null}
        Text me the first message
      </Button>
    </Shell>
  );
}
